/** Java LocalDateTime JSON (`2026-09-16T15:04:05` or `2026-09-16T15:04:05.123`). */
const NAIVE_DATE_TIME =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

function localTimeZone(): string | undefined {
  if (typeof Intl === 'undefined') {
    return undefined;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Parse API visit timestamps. Naive ISO date-times (no Z/offset) are UTC from the
 * server {@code LocalDateTime}; display helpers then convert to the user's zone.
 */
export function parseApiDateTime(raw: string | Date | number | null | undefined): Date | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (raw instanceof Date) {
    return Number.isFinite(raw.getTime()) ? raw : null;
  }
  if (typeof raw === 'number') {
    const fromMs = new Date(raw);
    return Number.isFinite(fromMs.getTime()) ? fromMs : null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  if (DATE_ONLY.test(s)) {
    const [year, month, day] = s.split('-').map(Number);
    const local = new Date(year, month - 1, day);
    return Number.isFinite(local.getTime()) ? local : null;
  }
  if (NAIVE_DATE_TIME.test(s) && !HAS_ZONE.test(s)) {
    const utc = new Date(`${s.replace(' ', 'T')}Z`);
    return Number.isFinite(utc.getTime()) ? utc : null;
  }
  const parsed = new Date(s);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function formatLocalTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: localTimeZone(),
  });
}

export function formatLocalDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: localTimeZone(),
    ...options,
  });
}

export function formatLocalDateAtTime(date: Date): string {
  const datePart = formatLocalDate(date);
  const timePart = formatLocalTime(date);
  return `${datePart} at ${timePart}`;
}
